import {
	type Container,
	fetchAccountsByAaId,
	fetchOutboundAuthData,
	type GetAccountsByAaIDResponse,
	type OAuthServiceAccount,
	type OAuthSetup,
} from '@atlassian/search-client';
import { getEnvironment } from '@atlassian/search-common';

const GITHUB_CONTAINER_ID_PROD =
	'c364f950-69f5-4812-9716-ed71a8d3d408_a57ca83d-597f-4de9-8dca-f9d3cd18d3a7';
const GITHUB_CONTAINER_ID_STAGING =
	'9bb160f8-1abb-4dc8-9780-ca5327e7ccc8_91300e36-3710-4f7b-aba2-95f43be1ac6f';

export type OutboundAuthCallStrategy = 'container-specific' | 'all-containers';

export type CallOutboundAuthParams = {
	aaid: string;
	strategy: OutboundAuthCallStrategy;
	degradationCallback: (reason: string) => void;
	cacheAnalyticsCallback: (state: { hit: boolean; refreshTriggered: boolean }) => Promise<void>;
};

/**
 * Checks if a service has available GitHub accounts
 * @param services - Array of services to check
 * @returns boolean - true if GitHub service has available or refreshNeeded accounts
 */
const hasAvailableGithubAccount = (services?: OAuthSetup[]): boolean => {
	return !!services
		?.find((s: OAuthSetup) => s?.service?.serviceKey === 'github')
		?.accounts?.some(
			(a: OAuthServiceAccount) => a?.status === 'available' || a?.status === 'refreshNeeded',
		);
};

/**
 * Calls outbound-auth service to check GitHub access
 * @param params - Configuration for the outbound-auth call
 * @returns Promise<boolean> - true if user has available GitHub account
 */
export const checkGithubSmartlinkAccess = async ({
	aaid,
	strategy,
	degradationCallback,
	cacheAnalyticsCallback,
}: CallOutboundAuthParams): Promise<boolean> => {
	if (strategy === 'container-specific') {
		const targetContainerId =
			getEnvironment() === 'production' ? GITHUB_CONTAINER_ID_PROD : GITHUB_CONTAINER_ID_STAGING;

		const data = await fetchOutboundAuthData({
			aaid,
			containerId: targetContainerId,
			serviceKey: 'github',
			degradationCallback,
			cacheAnalyticsCallback,
		});

		return hasAvailableGithubAccount(data?.services);
	} else if (strategy === 'all-containers') {
		const data: GetAccountsByAaIDResponse = await fetchAccountsByAaId({
			aaid,
			degradationCallback,
			cacheAnalyticsCallback,
		});

		return !!data?.containers?.some((container: Container) =>
			hasAvailableGithubAccount(container.services),
		);
	}
	return false;
};
